<template>
  <div class="final-review" v-if="order && content">
    <Spinner name="circle" color="var(--color-links)" v-if="loading" />

    <div class="container" v-if="cartExpired">
      <p>
        {{
          content.checkoutExpiredHeader ||
          'Sorry, but due to inactivity and for your security, your cart has expired'
        }}
      </p>

      <a class="button button--primary" href="/"> Leave checkout </a>
    </div>

    <section class="checkout" v-if="!cartExpired">
      <div class="container">
        <div class="checkout--content">
          <fieldset>
            <legend>
              <span>{{ content.orderSummaryLabel || 'Order Summary' }}</span>
            </legend>

            <div class="summary">
              <div class="summary--header">
                <span>{{ content.currencyLabel || 'Currency' }}</span>
                <span>{{ content.amountLabel || 'Amount' }}</span>
                <span v-if="showLoyaltyCTA && isSignedInToLoyalty">
                  {{
                    content.loyaltyRateLabel || 'Loyalty Rate'
                  }}
                </span>
                <span v-else>
                  {{ content.standardRateLabel || 'Rate' }}
                </span>
                <div>
                  <span v-if="order.product.id !== ProductTypes.SELL_BACK">{{
                    content.priceLabel || 'Price'
                  }}</span>
                  <span v-else>{{ content.amountFromSellbackLabel || 'You\'ll get' }}</span>
                </div>
              </div>

              <div class="summary--items">
                <div
                  class="summary--item"
                  v-for="(line, index) in order.lines"
                  :key="index"
                >
                  <div>
                    <span :class="`icon icon__${line.currency.code.toLowerCase()}`" />
                    <span class="code">{{ line.currency.code }}</span>
                  </div>
                  <span>{{ line.qty }}</span>
                  <span>{{ line.rate | rate }}</span>
                  <span>{{ line.total | price }}</span>
                </div>
                <div
                  class="summary--buyback"
                  v-if="COMMERCE_SHOW_BUYBACK && order.buyBackGuarantee"
                >
                  <label>{{ content.buybackGuaranteeLabel || "Buyback Guarantee" }}</label>
                  <p>{{ 4 | price }}</p>
                </div>
                <div
                  class="summary--buyback"
                  v-if="COMMERCE_SHOW_REFUND && order.refundGuarantee"
                >
                  <label>{{ content.refundGuaranteeLabel || "Refund Guarantee" }}</label>
                  <p>{{ 3 | price }}</p>
                </div>

                <div class="summary--buyback">
                  <label v-if="order.product.id === ProductTypes.HOME_DELIVERY">
                    {{ content.homeDeliveryLabel || 'Standard delivery' }}
                  </label>
                  <label v-if="order.product.id === ProductTypes.CLICK_AND_COLLECT">
                    {{ content.clickAndCollectLabel || 'Branch collection' }}
                  </label>
                  <label v-if="order.product.id === ProductTypes.SELL_BACK">
                    {{
                      content.sellbackAndCollectLabel || 'Sell back and collect'
                    }}
                  </label>
                  <p>
                    {{ order.deliverySlot.price | price }}
                  </p>
                </div>
                <div class="summary--total">
                  <label v-if="order.product.id !== ProductTypes.SELL_BACK">{{ content.totalAmountToPayLabel || "Total to pay" }}</label>
                  <label v-else>{{ content.totalAmountFromSellbackLabel || "Total you will receive" }}</label>
                  <p class="total-price">{{ order.total | price }}</p>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>
              <span>{{ content.deliveryMethodLabel || 'Delivery Method' }}</span>
              <a v-if="order.state.value === OrderStates.CREATED" href="#" @click="navigate('checkout-select-delivery-slot')">{{ content.amendDetailsLinkText || "Amend my details" }}</a>
            </legend>
            <div class="grid">
              <div>
                <div v-if="order.product.id === ProductTypes.HOME_DELIVERY">
                  <label>
                    {{ content.homeDeliveryLabel || 'Delivery Address' }}
                  </label>
                  <delivery-address :order="order" />
                </div>
                <div v-else>
                  <label>
                    {{ content.clickAndCollectLabel || 'Collection Address' }}
                  </label>
                  <store-address :storeAddress="storeAddress" />
                </div>
              </div>
              <div>
                <div v-if="order.product.id === ProductTypes.HOME_DELIVERY">
                  <label>
                    {{ content.deliveryDateLabel || 'Delivery date' }}
                  </label>
                  <p>{{ order.deliverySlot.date | date }}</p>
                </div>
                <div v-else>
                  <label>
                    {{ content.collectionDateLabel || 'Collection date' }}
                  </label>
                  <p>{{ order.deliverySlot.date | date }}</p>
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>
              <span>{{ content.contactDetailsLabel || 'Contact Details' }}</span>
              <a v-if="order.state.value === OrderStates.CREATED" href="#" @click="navigate('checkout-customer-information', 'contact-details')">{{ content.amendDetailsLinkText || "Amend my details" }}</a>
            </legend>

            <div class="grid">
              <div>
                <label>{{ content.emailAddressLabel || "Email address" }}</label>
                <p>{{ order.customer.email }}</p>
              </div>
              <div>
                <label>{{ content.phoneNumberLabel || "Phone number" }}</label>
                <p>
                  {{ order.customer.phoneNumber }}
                </p>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>
              <span>{{ content.aboutYouLabel || 'About You' }}</span>
              <a v-if="order.state.value === OrderStates.CREATED" href="#" @click="navigate('checkout-customer-information', 'about-you')">{{ content.amendDetailsLinkText || "Amend my details" }}</a>
            </legend>
            <div class="grid">
              <div>
                <label>{{ content.firstNameLabel || "First name" }}</label>
                <p>{{ order.customer.firstName }}</p>
              </div>
              <div>
                <label>{{ content.lastNameLabel || "Last name" }}</label>
                <p>{{ order.customer.lastName }}</p>
              </div>
              <div>
                <label>{{ content.dateOfBirthLabel || "Date of birth" }}</label>
                <p>{{ (new Date(order.customer.dateOfBirth)).toLocaleDateString() }}</p>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>
              <span>{{ content.billingAddressLabel || 'Billing Address' }}</span>
              <a v-if="order.state.value === OrderStates.CREATED" href="#" @click="navigate('checkout-customer-information', 'billing-address')">{{ content.amendDetailsLinkText || "Amend my details" }}</a>
            </legend>

            <div class="grid">
              <div>
                <customer-address :order="order" />
              </div>
            </div>
          </fieldset>

          <div class="final-review--next">
            <Spinner name="circle" color="var(--color-links)" v-if="loading" />
            <button
              class="button button--primary"
              @click="next()"
              v-if="!loading"
            >
              {{ content.nextButtonLabel || "Next" }}
            </button>
            <div v-if="!loading && devicePayEnabled && order.product.id !== ProductTypes.SELL_BACK">
              <div class="pay-divider">
                <span>or</span>
              </div>
              <div class="pay-buttons">
                <ApplePayWidget v-show="applePay.isEnabled && showApplePay"
                  :amount="String(order.total)"
                  :merchantIdentifier="applePay.merchantId"
                  :merchantName="applePay.merchantName"
                  :currencyCode="applePay.currencyCode"
                  :countryCode="applePay.countryCode"
                  :acceptCreditCards="acceptCreditCards"
                  @available="showApplePay = $event"
                  @success="handleSuccess"
                  @error="handleError"
                  @cancel="handleCancel"
                  @confirmOrder="onConfirmOrder"
                  @3ds="onThreeDSRedirect"
                  @click="disablePayButtons"
                />
                <GooglePayWidget
                  v-show="googlePay.isEnabled && showGooglePay"
                  :environment="googlePay.productionMode ? 'PRODUCTION' : 'TEST'"
                  :amount="String(order.total)"
                  :currencyCode="googlePay.currencyCode"
                  :countryCode="googlePay.countryCode"
                  :acceptCreditCards="acceptCreditCards"
                  :merchantId="googlePay.merchantId"
                  :merchantName="googlePay.merchantName"
                  :gatewayMerchant="googlePay.gatewayMerchant"
                  :gatewayMerchantId="googlePay.gatewayMerchantId"
                  @available="showGooglePay = $event"
                  @success="handleSuccess"
                  @error="handleError"
                  @cancel="handleCancel"
                  @3ds="onThreeDSRedirect"
                  @confirmOrder="onConfirmOrder"
                  @click="disablePayButtons"
                />

                <ThreeDSModal
                  :url="threeDSUrl"
                  :visible="threeDSVisible"
                  @close="onThreeDSClose"
                  @complete="onThreeDSComplete"
                  @failed="onThreeDSFailed"
                />
                <div
                  v-if="paymentProcessing"
                  class="tds-overlay"
                >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getStoreAddress } from '@vue/services/api/store.api'
import { setConfirm } from '@vue/services/api/checkout.api'
import Spinner from 'vue-spinkit'
import OrderStates from '@vue/common/constants/order'
import { mapActions, mapMutations, mapState } from 'vuex'
import DeliveryAddress from '@vue/views/checkout/common/delivery-address'
import CustomerAddress from '@vue/views/checkout/common/customer-address'
import StoreAddress from '@vue/views/checkout/common/store-address'
import { ProductTypes } from '@vue/common/constants/productTypes'
import {
  COMMERCE_SHOW_BUYBACK,
  COMMERCE_SHOW_REFUND,
} from '@vue/common/constants/config'
import { ProblemTypes } from '@vue/common/constants/problemTypes'
import ThreeDSModal from '@vue/common/components/ThreeDSModal.vue'
import ApplePayWidget from '@vue/common/components/ApplePayWidget.vue'
import GooglePayWidget from '@vue/common/components/GooglePayWidget.vue'
import { showInfo, showError } from '@vue/services/toast.service';
import { SET_ORDER } from '@vue/common/constants/mutation.types'

export default {
  name: 'FinalReview',
  components: {
    GooglePayWidget,
    ApplePayWidget,
    ThreeDSModal,
    Spinner,
    DeliveryAddress,
    CustomerAddress,
    StoreAddress,
  },
  data() {
    return {
      COMMERCE_SHOW_BUYBACK: COMMERCE_SHOW_BUYBACK,
      COMMERCE_SHOW_REFUND: COMMERCE_SHOW_REFUND,
      ProductTypes: ProductTypes,
      paymentProcessing: false,
      loading: false,
      showApplePay: false,
      showGooglePay: false,
      devicePayEnabled: false,
      storeAddress: {
        address1: null,
        address2: null,
        address3: null,
        address4: null,
        postcode: null,
      },
      threeDSUrl: null,
      threeDSVisible: false,
      acceptCreditCards: false,
      applePay: {
        isEnabled: false,
        currencyCode: null,
        countryCode: null,
        merchantName: null,
        merchantId: null
      },
      googlePay: {
        isEnabled: false,
        productionMode: false,
        currencyCode: null,
        countryCode: null,
        merchantName: null,
        merchantId: null,
        gatewayMerchant: null,
        gatewayMerchantId: null
      },
      OrderStates: OrderStates
    }
  },
  computed: {
    ...mapState({
      content: (state) => state.checkout.content,
      order: (state) => state.checkout.order,
      cartExpired: (state) => state.cart.cartExpired,
      isSignedInToLoyalty: (state) => state.calculator.isSignedInToLoyalty,
    }),
    showLoyaltyCTA() {
      return window.calculatorSettings?.showLoyaltyCTA || false
    },
  },
  methods: {
    ...mapMutations({
      updateStep: 'checkout/updateStep',
      expiredOrder: 'checkout/expiredOrder',
      setOrder: `checkout/${SET_ORDER}`,
    }),
    ...mapActions({
      initSession: 'session/init',
      initCalculator: 'calculator/init',
      getOrder: 'checkout/getOrder',
    }),
    disablePayButtons() {
      this.paymentProcessing = true;
    },
    clearCachedFormValues() {
      window.sessionStorage.removeItem("houseNameNumber");
      window.sessionStorage.removeItem("addressLine1");
    },
    onConfirmOrder({_, resolve, reject}) {
      console.log('onConfirmOrder: order state', this.order.state.value);

      if (this.order.state.value === OrderStates.PAYMENT_PENDING) {
        resolve?.();
        return;
      }

      if (this.order.state.value === OrderStates.CHECKOUT_FAILED) {
        reject?.();
        window.location = window.checkoutWorkflow.error;
        return;
      }

      setConfirm().then(({data}) => {
        this.setOrder(data);
        if (this.order.state.value === OrderStates.PAYMENT_PENDING) {
          resolve?.();
          return;
        }

        if (this.order.state.value === OrderStates.COMPLETED) {
          window.location = window.checkoutWorkflow.next;
          return;
        }

        reject?.();
        window.location = window.checkoutWorkflow.error;
      }).catch(err => {
        const response = err.response;
        if(response?.status === 409) {
          if(response?.data?.type?.includes(ProblemTypes.OUT_OF_STOCK)) {
            showError(this.content.checkoutFailedHeader || "Checkout Failed",
              this.content.outOfStockMessage || "One or more lines on your order are out of stock.");
          } else {
            showError(response.data.title, response.data.detail);
          }

          reject?.();
          setTimeout(() => {
            window.location = window.checkoutWorkflow.error
          }, 4000);
        } else {
          showError(this.content.checkoutFailedHeader || "Checkout Failed",
            this.content.unexpectedErrorText || "An unexpected error occurred.");
          reject?.();
        }
      });
    },
    next() {
      this.clearCachedFormValues();

      if (this.order.state.value === OrderStates.PAYMENT_PENDING) {
        window.location = window.checkoutWorkflow.next;
        return;
      }
      setConfirm().then(({ data }) => {
        this.setOrder(data);
        if(this.order.state.value === OrderStates.PAYMENT_PENDING) {
          window.location = window.checkoutWorkflow.next;
          return;
        }

        if(this.order.state.value === OrderStates.COMPLETED) {
          window.location = window.checkoutWorkflow.next;
          return;
        }

        window.location = window.checkoutWorkflow.error
      }).catch(err => {
        const response = err.response;
        if(response?.status === 409) {
          if(response?.data?.type?.includes(ProblemTypes.OUT_OF_STOCK)) {
            showError(this.content.checkoutFailedHeader || "Checkout Failed", 
                      this.content.outOfStockMessage || "One or more lines on your order are out of stock.");
          } else {
            showError(response.data.title, response.data.detail);
          }

          setTimeout(() => {
            window.location = window.checkoutWorkflow.error
          }, 4000);
        } else {
          showError(this.content.checkoutFailedHeader || "Checkout Failed", 
                    this.content.unexpectedErrorText || "An unexpected error occurred.");
          throw err;
        }
      });
    },
    navigate(stepName, fragment) {
      const url = `${window.checkoutWorkflow?.steps[stepName]}`;
      window.location = fragment ? `${url}#${fragment}` : url;
    },
    onThreeDSRedirect({ url }) {
      this.threeDSUrl = url;
      this.threeDSVisible = true;
      this.paymentProcessing = false;
    },

    async onThreeDSClose() {
      await this.resetThreeDS();
    },

    async onThreeDSFailed() {
      this.handleError({stage: 'paymentAuthorized', error: 'Failed 3DS check'});
      await this.resetThreeDS()
    },

    async resetThreeDS() {
      this.threeDSVisible = false;
      this.threeDSUrl = null;
      await this.getOrder();
      this.paymentProcessing = false;
    },

    onThreeDSComplete() {
      this.onThreeDSClose();
      window.location = window.checkoutWorkflow.steps['checkout-order-confirmation'];
    },

    handleSuccess(payment) {
      if (payment.redirectUrl) {
        window.location = payment.redirectUrl;
      }
    },

    handleError({ stage, error }) {
      if (stage === 'paymentAuthorized') {
        showError('Unable to authorise payment. Please try a different card')
      } else {
        showError('Error during payment. Please try again')
      }
      this.paymentProcessing = false;
      console.error(error, stage);
    },

    handleCancel() {
      showInfo({text: 'Payment attempt cancelled'});
      console.log('Payment session cancelled');
      this.paymentProcessing = false;
    },
  },
  async mounted() {
    await this.initSession()
    await this.initCalculator();
    this.updateStep('finalReview')
    this.loading = true
    await this.getOrder()
    if (
      this.order.product.id !== ProductTypes.HOME_DELIVERY &&
      this.order.store?.id
    ) {
      const storeAddressResult = await getStoreAddress(this.order.store.id)
      this.storeAddress = storeAddressResult?.data
    }

    this.acceptCreditCards = window.devicePaymentSettings?.acceptCreditCards || false;
    this.applePay = window.applePaySettings;
    this.googlePay = window.googlePaySettings;
    this.devicePayEnabled = this.applePay.isEnabled || this.googlePay.isEnabled;
    this.loading = false;
    console.log('Finished loading');
  },
}
</script>
